import React, { FC, useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'

import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { Typography, Grid } from '@mui/material'

import { IBankAccount, IUserBankAccount } from '@sparling/financial-common'

import { columns } from './BankAccountsList'
import { ViewBankAccountModal } from './ViewBankAccountModal'
import { EditBankAccountStatusModal } from './EditBankAccountStatusModal'
import { IDataItem, TableComponent } from '../../../../components/Table'
import { getUserBankAccountsRequest } from '../../../../store/actions'
import { getUserBankAccounts, userBankAccountsIsLoading } from '../../../../store/selectors'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    marginTop: theme.spacing(2),
    padding: theme.spacing(4),
    height: '100%',
  },
  titleWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    minWidth: 400,
  },
  table: {
    paddingTop: theme.spacing(2),
  },
  buttonWrap: {
    justifyContent: 'flex-end',
  },
  button: {
    justifyContent: 'flex-end',
  },
}))

export const BankAccounts: FC = () => {
  const classes = useStyles()
  const { clientId: idSf } = useParams<{ clientId: string }>()
  const dispatch = useDispatch()
  const isLoading = useSelector(userBankAccountsIsLoading)
  const { items: userBankAccountsData, meta: params } = useSelector(getUserBankAccounts)
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null)
  const [isOpenViewBankAccountModal, setOpenViewBankAccountModal] = useState<boolean>(false)
  const [isOpenEditBankAccountStatusModal, setOpenEditBankAccountStatusModal] = useState<boolean>(false)

  const loadUserBankAccounts = useCallback(
    (page = 1) => {
      if (idSf) {
        dispatch(getUserBankAccountsRequest({ idSf, page }))
      }
    },
    [dispatch, idSf],
  )

  useEffect(() => {
    loadUserBankAccounts()
  }, [loadUserBankAccounts])

  const getSelectedElementIdHandle = useCallback((id) => {
    setSelectedAccountId(id)
  }, [])

  const openViewBankAccountModalHandle = useCallback((id?: string | number) => {
    if (id) {
      setOpenViewBankAccountModal(true)
      setSelectedAccountId(id as number)
    }
  }, [])

  const closeViewBankAccountModalHandle = useCallback(() => {
    setOpenViewBankAccountModal(false)
  }, [])

  const openEditBankAccountStatusModalHandle = useCallback((id: string | number) => {
    setSelectedAccountId(id as number)
    setOpenEditBankAccountStatusModal(true)
  }, [])

  const closeEditBankAccountStatusModalHandle = useCallback(() => {
    setOpenEditBankAccountStatusModal(false)
  }, [])

  return (
    <>
      <Grid container className={classes.container}>
        <Grid container className={classes.titleWrap}>
          <Typography variant={'h6'}>Bank Accounts</Typography>
        </Grid>

        <Grid container className={classes.table}>
          <TableComponent
            getElementId={getSelectedElementIdHandle}
            columns={columns}
            data={userBankAccountsData as IDataItem<IUserBankAccount>[]}
            params={params}
            onInfo={openViewBankAccountModalHandle}
            onEdit={openEditBankAccountStatusModalHandle}
            loading={isLoading}
            loadData={loadUserBankAccounts}
          />
        </Grid>
      </Grid>

      {isOpenViewBankAccountModal && (
        <ViewBankAccountModal
          isOpen={true}
          modalClose={closeViewBankAccountModalHandle}
          fields={userBankAccountsData.find((bankAccount) => bankAccount.id === selectedAccountId) as IBankAccount}
        />
      )}

      {isOpenEditBankAccountStatusModal && (
        <EditBankAccountStatusModal
          isOpen={true}
          modalClose={closeEditBankAccountStatusModalHandle}
          fields={userBankAccountsData.find((bankAccount) => bankAccount.id === selectedAccountId) as IUserBankAccount}
        />
      )}
    </>
  )
}
